import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, MapPin, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { getRecentlyViewed, getTimeAgo } from '../utils/recentlyViewed';
import LazyImage from './LazyImage';

const RecentlyViewed = () => {
  const [recentVenues, setRecentVenues] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    loadRecentVenues();
  }, []);

  const loadRecentVenues = () => {
    const venues = getRecentlyViewed();
    setRecentVenues(venues.slice(0, 6)); // Show max 6
  };

  const scroll = (direction) => {
    const container = document.getElementById('recently-viewed-scroll');
    if (container) {
      const scrollAmount = 300;
      if (direction === 'left') {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    }
  };

  if (recentVenues.length === 0) return null;

  return (
    <div className="bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Eye className="w-6 h-6 text-pink-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Son Baktığınız Davet Evleri
            </h2>
          </div>
          
          {/* Scroll buttons */}
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 rounded-full bg-white shadow hover:shadow-md transition-all"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 rounded-full bg-white shadow hover:shadow-md transition-all"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Horizontal scroll container */}
        <div
          id="recently-viewed-scroll"
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {recentVenues.map((venue, index) => {
            const citySlug = venue.cityName?.toLowerCase()
              .replace(/ı/g, 'i')
              .replace(/ğ/g, 'g')
              .replace(/ü/g, 'u')
              .replace(/ş/g, 's')
              .replace(/ö/g, 'o')
              .replace(/ç/g, 'c')
              .replace(/İ/g, 'i')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');
            
            const districtSlug = venue.districtName?.toLowerCase()
              .replace(/ı/g, 'i')
              .replace(/ğ/g, 'g')
              .replace(/ü/g, 'u')
              .replace(/ş/g, 's')
              .replace(/ö/g, 'o')
              .replace(/ç/g, 'c')
              .replace(/İ/g, 'i')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');

            const venueUrl = `/${citySlug}/${districtSlug}/${venue.slug}`;

            return (
              <Link
                key={venue._id}
                to={venueUrl}
                className="group flex-shrink-0 w-72 bg-white rounded-xl shadow hover:shadow-lg transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-48 rounded-t-xl overflow-hidden">
                  {venue.images ? (
                    <LazyImage
                      src={venue.images}
                      alt={venue.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <MapPin className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Viewed time badge */}
                  {venue.viewedAt && (
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {getTimeAgo(venue.viewedAt)}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-1 group-hover:text-pink-600 transition-colors">
                    {venue.name}
                  </h3>
                  
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{venue.districtName}, {venue.cityName}</span>
                  </div>

                  {venue.rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-semibold">{venue.rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RecentlyViewed;
